// app.origins — the browser origins this project's bucket accepts.
//
// WHY THIS EXISTS. The SDK calls S3 straight from the browser, so every origin
// an app is served from must be in the bucket's CORS rule or every read and
// write fails. Provisioning seeds a good default list, and `domains add`
// appends a custom domain's origin — but neither covers an app wrapped in a
// NATIVE SHELL. Capacitor, Electron, Tauri and browser extensions each serve
// from an origin that is not a DNS host at all (`capacitor://localhost`,
// `tauri://localhost`, `chrome-extension://<id>`), so `domains add` has nothing
// to attach and the tenant was blocked until the platform shipped a release
// naming their shell. The enumeration is open-ended — an extension's id can't
// even be known ahead of time — so a per-project door is the only shape that
// scales.
//
// WHY IT'S A DIRECT S3 CALL, not a platform request like `domains`. Domain
// attach needs the provisioning secret (ACM, CloudFront, Route53 — resources
// outside this project), so it must ride the interchange. Bucket CORS is a
// property of the project's OWN bucket, and IAM can express exactly that:
// `s3:PutBucketCORS` on this bucket only, granted by the `manage-domains`
// capability. So it follows the platform's default bias — if IAM can express
// it, IAM enforces it and the browser calls AWS directly. No server function.
//
// WHAT THIS IS NOT. CORS is not the access boundary. Every S3 call is
// SigV4-signed with STS credentials from the Identity Pool, so a foreign origin
// cannot make an authenticated request whatever this list says, and CORS binds
// browsers only — curl and servers ignore it. IAM is the boundary. This list is
// defence-in-depth and audit posture, which is why `add` refuses a bare `*`.

import {
  GetBucketCorsCommand,
  PutBucketCorsCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import {
  assertCustomOrigin,
  defaultBucketOrigins,
  normalizeOrigin,
} from "./conventions.ts";

/** The CORS methods/headers every s3p bucket rule carries. Kept identical to
 *  the provisioner's (`ensureBucketCors`) so a read-modify-write from this
 *  module never silently narrows a rule the provisioner wrote. */
const CORS_METHODS = ["GET", "HEAD", "PUT", "POST", "DELETE"];
const CORS_EXPOSE_HEADERS = ["ETag", "x-amz-request-id", "x-amz-version-id"];
const CORS_MAX_AGE = 3000;

export interface OriginEntry {
  origin: string;
  /** Seeded and re-asserted by the platform (provision + converge). Not
   *  removable — `remove()` refuses rather than letting converge silently
   *  undo it later. */
  managed: boolean;
}

export interface OriginsModule {
  /** Every origin this bucket currently accepts, flagged by who owns it. */
  list(): Promise<OriginEntry[]>;
  /** Allow an origin. Idempotent. Throws on a bare wildcard or a non-origin
   *  string. Returns the normalized value actually stored. */
  add(origin: string): Promise<string>;
  /** Stop allowing an origin. Refuses platform-managed entries. Returns false
   *  when the origin wasn't in the list to begin with. */
  remove(origin: string): Promise<boolean>;
}

export function createOrigins(cfg: {
  getClient: () => Promise<S3Client>;
  bucket: string;
  slug: string;
  region: string;
}): OriginsModule {
  const managedSet = () =>
    new Set(
      defaultBucketOrigins({
        region: cfg.region,
        bucket: cfg.bucket,
        slug: cfg.slug,
      }).map(normalizeOrigin),
    );

  async function read(): Promise<string[]> {
    const s3 = await cfg.getClient();
    try {
      const cur = await s3.send(new GetBucketCorsCommand({ Bucket: cfg.bucket }));
      return (cur.CORSRules ?? []).flatMap((r: { AllowedOrigins?: string[] }) => r.AllowedOrigins ?? []);
    } catch (e) {
      // NoSuchCORSConfiguration is "no origins yet", not a failure. Anything
      // else (AccessDenied on a role without manage-domains) must surface —
      // swallowing it would render an empty list that looks like a healthy
      // bucket with no origins, which is the one state that cannot happen.
      if ((e as { name?: string })?.name === "NoSuchCORSConfiguration") return [];
      throw e;
    }
  }

  async function write(origins: string[]): Promise<void> {
    const s3 = await cfg.getClient();
    await s3.send(
      new PutBucketCorsCommand({
        Bucket: cfg.bucket,
        CORSConfiguration: {
          CORSRules: [
            {
              AllowedOrigins: origins,
              AllowedMethods: CORS_METHODS,
              AllowedHeaders: ["*"],
              ExposeHeaders: CORS_EXPOSE_HEADERS,
              MaxAgeSeconds: CORS_MAX_AGE,
            },
          ],
        },
      }),
    );
  }

  return {
    async list() {
      const managed = managedSet();
      const seen = new Set<string>();
      const out: OriginEntry[] = [];
      for (const raw of await read()) {
        const o = normalizeOrigin(raw);
        if (seen.has(o)) continue;
        seen.add(o);
        out.push({ origin: o, managed: managed.has(o) });
      }
      // Managed origins the bucket is currently MISSING are still reported, so
      // the console/CLI can show a project that predates a platform default
      // (exactly the Capacitor case) instead of quietly omitting it.
      for (const m of managed) {
        if (!seen.has(m)) out.push({ origin: m, managed: true });
      }
      return out.sort((a, b) => a.origin.localeCompare(b.origin));
    },

    async add(origin) {
      const o = assertCustomOrigin(origin);
      const current = (await read()).map(normalizeOrigin);
      if (current.includes(o)) return o;
      // Union, never replace: a concurrent `domains add` or converge may have
      // written origins between our read and our write. This races in the same
      // benign way `ensureBucketCors` does — last writer wins, and converge
      // re-asserts the managed set regardless.
      await write([...new Set([...current, o])].sort());
      return o;
    },

    async remove(origin) {
      const o = normalizeOrigin(origin);
      if (managedSet().has(o)) {
        throw new Error(
          `origin: '${o}' is managed by the platform and cannot be removed (it is re-asserted on every converge).`,
        );
      }
      const current = (await read()).map(normalizeOrigin);
      if (!current.includes(o)) return false;
      await write(current.filter((x) => x !== o).sort());
      return true;
    },
  };
}
